
import { useState, useCallback, useMemo } from "react";
import type { ColumnFiltersState } from "@tanstack/react-table";
import type { FilterValue, FilterFieldDefinition } from "@/components/ui/filter-bar";
import { useDebouncedValue } from "./useDebouncedValue";

const DEFAULT_OP: Record<string, string> = {
  enum: "is",
  relation: "is",
  text: "contains",
  number: "eq",
  date: "is",
};

const OP_PREFIX: Record<string, string> = {
  is_not: "!=",
  not_contains: "not_contains",
  neq: "!=",
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<=",
  before: "<",
  after: ">",
  on: "",
};

/**
 * Encode a UI filter into the string TanStack's filter functions understand.
 * Shared by the toolbar chips and the header filter menu so both paths write
 * exactly the same `columnFilters` value.
 */
export function encodeFilterValue(
  field: FilterFieldDefinition | undefined,
  operator: string,
  values: string[]
): string {
  const defaultOp = field ? DEFAULT_OP[field.type] : "is";

  if (operator === "between" && values.length >= 2) {
    return `between:${values[0]}|${values[1]}`;
  }

  if (operator !== defaultOp) {
    const prefix = OP_PREFIX[operator];
    if (prefix) {
      return `${prefix}:${values.join(",")}`;
    }
  }

  return values.join(",");
}

function decodeOperator(prefix: string, type?: FilterFieldDefinition["type"]): string {
  switch (prefix) {
    case "!=":
      return type === "number" ? "neq" : "is_not";
    case "contains":
      return "contains";
    case "not_contains":
      return "not_contains";
    case ">":
      return type === "number" ? "gt" : "after";
    case ">=":
      return "gte";
    case "<":
      return type === "number" ? "lt" : "before";
    case "<=":
      return "lte";
    default:
      return "is";
  }
}

/**
 * Decode a TanStack `columnFilters` value back into the toolbar's FilterValue.
 * Handles both the toolbar shorthand (bare values) and the header menu's
 * explicit operator prefix.
 */
export function decodeFilterValue(
  id: string,
  value: string,
  field?: FilterFieldDefinition
): FilterValue {
  const raw = String(value ?? "");

  if (raw.startsWith("between:")) {
    const [min = "", max = ""] = raw.slice("between:".length).split("|");
    return {
      fieldId: id,
      operator: "between",
      values: [min, max].filter((entry) => entry.length > 0),
    };
  }

  const prefixed = /^(!=|>=|<=|contains|not_contains|>|<):(.*)$/.exec(raw);
  if (prefixed) {
    return {
      fieldId: id,
      operator: decodeOperator(prefixed[1], field?.type),
      values: prefixed[2].split(","),
    };
  }

  if (field?.type === "date") {
    const isExplicitDate = /^\d{4}-\d{2}-\d{2}$/.test(raw);
    return {
      fieldId: id,
      operator: isExplicitDate ? "on" : "is",
      values: raw.split(","),
    };
  }

  return {
    fieldId: id,
    operator: field ? DEFAULT_OP[field.type] : "is",
    values: raw.split(","),
  };
}

export function useFilterBar(filterFields: FilterFieldDefinition[]) {
  const [filters, setFilters] = useState<FilterValue[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebouncedValue(searchValue, 300);

  const fieldMap = useMemo(
    () => new Map(filterFields.map((f) => [f.id, f])),
    [filterFields]
  );

  const columnFilters: ColumnFiltersState = useMemo(
    () =>
      filters
        .filter((f) => f.values.length > 0)
        .map((f) => {
          const field = fieldMap.get(f.fieldId);
          const op = f.operator ?? (field ? DEFAULT_OP[field.type] : "is");

          return {
            id: f.fieldId,
            value: encodeFilterValue(field, op, f.values),
          };
        }),
    [filters, fieldMap]
  );

  const handleTableFiltersChange = useCallback(
    (next: ColumnFiltersState) => {
      setFilters(
        next.flatMap((filter) => {
          if (filter.value == null || String(filter.value).length === 0) return [];
          return [
            decodeFilterValue(
              filter.id,
              String(filter.value),
              fieldMap.get(filter.id)
            ),
          ];
        })
      );
    },
    [fieldMap]
  );

  const matchesSearch = useCallback(
    (...texts: (string | undefined | null)[]) => {
      if (!debouncedSearch) return true;
      const term = debouncedSearch.toLowerCase();
      return texts.some((t) => t?.toLowerCase().includes(term));
    },
    [debouncedSearch]
  );

  const matchesFilter = useCallback(
    (fieldId: string, value: string | undefined | null) => {
      const filter = filters.find((f) => f.fieldId === fieldId);
      if (!filter || filter.values.length === 0) return true;
      if (!value) return false;

      const field = fieldMap.get(fieldId);
      const op = filter.operator ?? (field ? DEFAULT_OP[field.type] : "is");
      const valueLower = value.toLowerCase();

      // Handle negation operators
      if (op === "is_not") {
        return !filter.values.some((v) => v.toLowerCase() === valueLower);
      }
      if (op === "not_contains") {
        return !filter.values.some((v) => valueLower.includes(v.toLowerCase()));
      }

      // Handle numeric operators
      if (op === "gt" || op === "gte" || op === "lt" || op === "lte" || op === "neq" || op === "eq") {
        const numValue = Number(value);
        const numFilter = Number(filter.values[0]);
        if (Number.isNaN(numValue) || Number.isNaN(numFilter)) return false;
        switch (op) {
          case "gt": return numValue > numFilter;
          case "gte": return numValue >= numFilter;
          case "lt": return numValue < numFilter;
          case "lte": return numValue <= numFilter;
          case "neq": return numValue !== numFilter;
          case "eq": return numValue === numFilter;
        }
      }

      // Handle between
      if (op === "between" && filter.values.length >= 2) {
        const num = Number(value);
        const min = Number(filter.values[0]);
        const max = Number(filter.values[1]);
        if (!Number.isNaN(num) && !Number.isNaN(min) && !Number.isNaN(max)) {
          return num >= min && num <= max;
        }
        // Date between
        return value >= filter.values[0] && value <= filter.values[1];
      }

      // Handle date operators
      if (op === "before") {
        return value < filter.values[0];
      }
      if (op === "after") {
        return value > filter.values[0];
      }
      if (op === "on") {
        const dateStr = new Date(value).toISOString().slice(0, 10);
        return dateStr === filter.values[0];
      }

      // Default: exact match (is / contains)
      if (op === "contains") {
        return filter.values.some((v) => valueLower.includes(v.toLowerCase()));
      }
      return filter.values.some((v) => v.toLowerCase() === valueLower);
    },
    [filters, fieldMap]
  );

  const filterBarProps = useMemo(
    () => ({
      filterFields,
      filters,
      onFiltersChange: setFilters,
      searchValue,
      onSearchChange: setSearchValue,
    }),
    [filterFields, filters, searchValue]
  );

  return {
    filters,
    setFilters,
    searchValue,
    setSearchValue,
    debouncedSearch,
    columnFilters,
    handleTableFiltersChange,
    matchesSearch,
    matchesFilter,
    filterBarProps,
  };
}
